/**
 * Wire protocol message types for the Configural runtime /ws gateway.
 *
 * The runtime owns the protocol; this file is a documentation-grade
 * mirror of what apps/runtime/src/gateway.js sends and accepts. Keep
 * the two in sync — when a new server-emitted message type lands in
 * the gateway, add it here and surface it as an event from CallClient.
 */

/** Client → Server. */
export const CLIENT_MSG = Object.freeze({
  /**
   * First message after WS open. Server resolves config from Mongo,
   * connects the LLM provider, then replies with `ready`.
   *
   * Shape: { type, tenantId, subTenantId, configMode: "draft"|"live",
   *          callMode: "voice"|"text" }
   */
  CONFIGURE: "configure",

  /**
   * Both voice + text modes: user input uses Gemini-Live's
   * passthrough envelope (the gateway's text handler parses
   * `msg.client_content.turns[].parts[].text` on the way to the
   * provider; the voice path uses `msg.realtime_input` for PCM).
   * Neither uses a top-level `type` field — the SDK builds the
   * envelope inside sendAudio() / sendText().
   */
});

/** Server → Client. */
export const SERVER_MSG = Object.freeze({
  /** Connection ready; call has started. Shape: { type, mode, callId }. */
  READY: "ready",

  /** Error during configure or call. Shape: { type, message }. */
  ERROR: "error",

  /** Voice mode: agent was interrupted by user speech. Shape: { type }. */
  INTERRUPTED: "interrupted",

  /** Either mode: a tool call completed.
   *  Shape: { type, name, args, result, time?, duration_ms? }. */
  TOOL_RESULT: "tool_result",

  /** Chain mode: agent handed off to a different agent.
   *  Shape: { type, agent, task? }. */
  SESSION_RESTARTED: "session_restarted",

  /** Text mode: streaming chunk. Shape: { type, text }. */
  TEXT_RESPONSE: "text_response",

  /** Text mode: end of agent's turn. Shape: { type, ... }. */
  TURN_COMPLETE: "turn_complete",

  /** Text mode: agent is thinking. Shape: { type }. */
  PROCESSING: "processing",

  /** Server is closing the call. Shape: { type, reason? }. */
  CLOSED: "closed",

  /** Adaptive-rate notice: server tells the (Twilio) bridge to change
   *  outbound playback speed. Emitted by apps/runtime/src/audio/adaptive-rate.js.
   *  Shape: { type, rate, reason? }. */
  RATE_CHANGE: "rate_change",
});

/**
 * Connection lifecycle states as exposed by CallClient.state.
 */
export const STATE = Object.freeze({
  IDLE:        "idle",
  CONNECTING:  "connecting",
  CONFIGURING: "configuring",
  READY:       "ready",
  CLOSING:     "closing",
  CLOSED:      "closed",
});
